// src/pages/Reminders.tsx
import { Fragment, useState, useEffect, useMemo, useRef } from "react";
import { supabase, getAuthToken } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import { useStaff } from "../lib/StaffContext";
import { usePracticienKey } from "../lib/useCrypto";
import { decryptFernet } from "../lib/fernetUtils";
import { resolveTemplateSenderName } from "../lib/templateMessageIdentity.mjs";
import { mapAppointmentsWithPlatoTemplateNames } from "../lib/platoPreferredName.mjs";
import {
  getRecallConversationRoleLabel,
  normalizeRecallConversationIdentifier,
  prepareRecallConversationMessages,
} from "../lib/recallConversationPreview.mjs";
import {
  findLatestReminderLogForRow,
  getReminderDeliveryLabel,
  isReminderDeliveryFailure,
  mergeReminderLogDeliveryUpdate,
  normalizeReminderDeliveryStatus,
  normalizeReminderPhone,
} from "../lib/reminderDeliveryStatus.mjs";
import * as XLSX from "xlsx";

type AppointmentItem = {
  id: string;
  calendar_color?: string;
  calendar_name?: string;
  patient_id: string;
  patient_name: string;
  patient_template_name?: string;
  patient_full_name?: string;
  patient_preferred_name?: string;
  patient_uses_preferred_name?: boolean;
  start: string;
  end: string;
  phone: string;
  practitioner?: string;
  all_day?: number;
  source?: string;
  appointment_status?: string;
  raw?: Record<string, any>;
};

type MergedAppointmentRow = {
  key: string;
  patient_name: string;
  patient_template_name?: string;
  patient_full_name?: string;
  patient_preferred_name?: string;
  patient_uses_preferred_name?: boolean;
  phone: string;
  time: string;
  sortStart: string;
  durationMinutes: number;
  calendars: string[];
  title: string;
  patient_id?: string;
  appointment_id?: string;
  appointment_status?: string;
};

type ReminderDraft = {
  title: string;
  patientName: string;
  doctorName: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentDuration: string;
};

type ClinicTemplate = {
  id: string;
  name: string;
  display_name?: string;
  template_body: string;
  placeholders: string[];
  chakra_template_name?: string;
  language_code?: string;
};

type ReminderLog = {
  id: string;
  patient_num: string;
  patient_name: string;
  appointment_id?: string;
  appointment_date: string;
  appointment_time?: string;
  doctor_name?: string;
  reminder_type: string;
  template_name?: string;
  message_sid?: string;
  sent_at: string;
  patient_responded: boolean;
  responded_at?: string;
  follow_up_count: number;
  last_follow_up_at?: string;
  delivery_status?: string;
  delivered_at?: string;
  read_at?: string;
};

type RemindersKpis = {
  total_sent: number;
  delivered_count: number;
  read_count: number;
  responded_count: number;
  delivery_rate: number;
  response_rate: number;
  avg_response_time_minutes: number;
  pending_follow_up_count: number;
};

type MonthlyPatient = {
  patient_id: string;
  patient_name: string;
  phone: string;
  visit_count: number;
  last_visit_date: string;
  doctor_name: string;
};

type RecallConversationMessage = {
  id: string;
  role: "user" | "assistant" | "human";
  contenu_chiffre: string;
  created_at: string;
};

type RecallConversationDisplayMessage = RecallConversationMessage & {
  content: string;
};

type RecallConversationPreviewState = {
  loading: boolean;
  messages: RecallConversationDisplayMessage[];
  error: string;
};

const REMINDER_TITLE_OPTIONS = ["", "Mr", "Mrs", "Ms", "Miss", "Mdm", "Madam", "Dr"];

const STANDARD_REMINDER_PLACEHOLDERS = new Set([
  "salutation", "patient_name", "doctor_name",
  "appointment_date", "appointment_time", "appointment_duration",
]);

const getExtraPlaceholders = (placeholders: string[]) =>
  placeholders.filter((p) => !STANDARD_REMINDER_PLACEHOLDERS.has(p));

const VALID_REMINDER_TITLES = new Set(
  REMINDER_TITLE_OPTIONS.filter(Boolean).map((v) => v.toLowerCase())
);

function normalizeReminderTitle(value: unknown): string {
  if (typeof value !== "string") return "";
  const cleaned = value.trim();
  if (!cleaned) return "";
  const lower = cleaned.toLowerCase();
  if (!VALID_REMINDER_TITLES.has(lower)) return "";
  if (lower === "mr") return "Mr";
  if (lower === "mrs") return "Mrs";
  if (lower === "ms") return "Ms";
  if (lower === "miss") return "Miss";
  if (lower === "mdm") return "Mdm";
  if (lower === "madam") return "Madam";
  if (lower === "dr") return "Dr";
  return "";
}

function parseDateSafely(value: string): Date | null {
  if (!value) return null;
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

const WEEKDAY_ABBR: Record<string, string> = {
  Monday: "Mon",
  Tuesday: "Tues",
  Wednesday: "Wed",
  Thursday: "Thur",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

function formatReminderDate(value?: string): string {
  const parsed = value ? parseDateSafely(value) : null;
  if (!parsed) return "";
  const weekdayFull = parsed.toLocaleDateString("en-GB", { weekday: "long" });
  const weekday = WEEKDAY_ABBR[weekdayFull] ?? weekdayFull;
  const day = parsed.getDate();
  const month = parsed.toLocaleDateString("en-GB", { month: "long" });
  const year = parsed.getFullYear();
  return `${weekday}, ${day} ${month} ${year}`;
}

function formatTime(value: string): string {
  if (!value) return "-";
  const parsed = parseDateSafely(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function formatDurationFromMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "-";
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
}

function getDurationMinutes(start?: string, end?: string): number {
  if (!start || !end) return 0;
  const startDate = parseDateSafely(start);
  const endDate = parseDateSafely(end);
  if (!startDate || !endDate) return 0;
  const diffMs = endDate.getTime() - startDate.getTime();
  if (!Number.isFinite(diffMs) || diffMs <= 0) return 0;
  return Math.round(diffMs / 60000);
}

function normalizePatientKey(name?: string, phone?: string): string {
  const normalizedName = (name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  const normalizedPhone = (phone || "").replace(/\D+/g, "");
  return `${normalizedName}::${normalizedPhone}`;
}

function extractAppointmentTitle(raw?: Record<string, any>): string {
  if (!raw || typeof raw !== "object") return "";
  const candidatePaths = [
    raw?.title,
    raw?.patient?.title,
    raw?.patient_data?.title,
    raw?.contact?.title,
    raw?.contact_data?.title,
    raw?.plato_patient_directory?.title,
    raw?.plato_patient_directory?.raw?.title,
    raw?.directory_patient?.title,
    raw?.directory_patient?.raw?.title,
    raw?.patient_directory?.title,
    raw?.patient_directory?.raw?.title,
    raw?.raw?.title,
    raw?.data?.title,
  ];
  for (const candidate of candidatePaths) {
    const normalized = normalizeReminderTitle(candidate);
    if (normalized) return normalized;
  }
  return "";
}

function subMonths(yearMonth: string, n: number): string {
  const [year, month] = yearMonth.split("-").map(Number);
  const date = new Date(year, month - 1 - n, 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString("en-SG", { month: "long", year: "numeric" });
}

function renderPreview(templateBody: string, placeholders: string[], variables: Record<string, string>): string {
  let rendered = templateBody;
  placeholders.forEach((key, index) => {
    const value = variables[key] || `{{${key}}}`;
    rendered = rendered.replace(`{{${index + 1}}}`, value);
    rendered = rendered.replace(`{{${key}}}`, value);
  });
  return rendered;
}

const API_URL = import.meta.env.VITE_API_URL as string;

export default function Reminders() {
  const user = useUser();
  const { activeStaff } = useStaff();
  const { key: practKey } = usePracticienKey();
  const praticienId = user?.id;
  const activeDoctor = JSON.parse(localStorage.getItem("activeDoctor") || "null");
  const templateSenderName = resolveTemplateSenderName(activeStaff, activeDoctor, user);

  const [activeTab, setActiveTab] = useState<
    "daily" | "dashboard" | "recallDashboard" | "monthly" | "recall"
  >("daily");

  // --- TAB 1: Daily reminders ---
  const [templates, setTemplates] = useState<ClinicTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ClinicTemplate | null>(null);
  const [appointmentsDate, setAppointmentsDate] = useState(() => {
    const now = new Date();
    const tzOffset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffset).toISOString().split("T")[0];
  });
  const [appointmentsItems, setAppointmentsItems] = useState<AppointmentItem[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState("");
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [reminderDrafts, setReminderDrafts] = useState<Record<string, ReminderDraft>>({});
  const [extraDraftVars, setExtraDraftVars] = useState<Record<string, Record<string, string>>>({});
  const [sentRows, setSentRows] = useState<Record<string, boolean>>({});
  const [sendingRows, setSendingRows] = useState<Record<string, boolean>>({});
  const [alreadySentToday, setAlreadySentToday] = useState<Set<string>>(new Set());
  const [dailyReminderLogs, setDailyReminderLogs] = useState<ReminderLog[]>([]);
  const [reminderRealtimeActive, setReminderRealtimeActive] = useState(false);
  const [phoneEdits, setPhoneEdits] = useState<Record<string, string>>({});
  const [validatedPhones, setValidatedPhones] = useState<Record<string, string>>({});
  const [savingPhones, setSavingPhones] = useState<Record<string, boolean>>({});

  // --- Status map clinique ---
  const [statusField, setStatusField] = useState("");
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});

  // --- Status update modal (Tab 1) ---
  const [statusUpdatePending, setStatusUpdatePending] = useState<{
    appointmentId: string;
    patientName: string;
  } | null>(null);
  const [pendingStatusCode, setPendingStatusCode] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);

  // --- TAB 2: Dashboard ---
  const [kpis, setKpis] = useState<RemindersKpis | null>(null);
  const [reminderLogs, setReminderLogs] = useState<ReminderLog[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [periodMode, setPeriodMode] = useState<"7" | "30" | "date">("30");
  const [dashboardDate, setDashboardDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [logsFilter, setLogsFilter] = useState<"all" | "noreply" | "replied">("all");
  const [followUpModalLog, setFollowUpModalLog] = useState<ReminderLog | null>(null);
  const [followUpTemplate, setFollowUpTemplate] = useState<ClinicTemplate | null>(null);
  const [followUpSending, setFollowUpSending] = useState(false);
  const [followUpSalutation, setFollowUpSalutation] = useState("");
  const [followUpPatientName, setFollowUpPatientName] = useState("");
  const [followUpDoctorName, setFollowUpDoctorName] = useState("");
  const [followUpAppointmentDate, setFollowUpAppointmentDate] = useState("");
  const [followUpAppointmentTime, setFollowUpAppointmentTime] = useState("");
  const [followUpDuration, setFollowUpDuration] = useState("");
  const [followUpExtraVars, setFollowUpExtraVars] = useState<Record<string, string>>({});
  const [dashboardStatusUpdating, setDashboardStatusUpdating] = useState<Record<string, boolean>>({});
  const [dashboardStatusSelected, setDashboardStatusSelected] = useState<Record<string, string>>({});
  const [dashboardApptStatus, setDashboardApptStatus] = useState<Record<string, string>>({});
  const [logsDisplayCount, setLogsDisplayCount] = useState(19);
  const [statusLoadingMore, setStatusLoadingMore] = useState(false);
  const lastPlatoCallRef = useRef<number>(0);

  // --- TAB 3: Monthly reminders ---
  const [scanMonth, setScanMonth] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  });
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState("");
  const [monthlyPatients, setMonthlyPatients] = useState<MonthlyPatient[]>([]);
  const [scanStats, setScanStats] = useState<{ patient_count: number; total_pages_fetched: number; cached: boolean } | null>(null);
  const [monthlyTemplate, setMonthlyTemplate] = useState<ClinicTemplate | null>(null);
  const [selectedMonthlyPatients, setSelectedMonthlyPatients] = useState<Set<string>>(new Set());
  const [monthlySentRows, setMonthlySentRows] = useState<Record<string, boolean>>({});
  const [monthlySendingRows, setMonthlySendingRows] = useState<Record<string, boolean>>({});
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);

  // --- TAB 4: Recall Import ---
  const [recallFile, setRecallFile] = useState<File | null>(null);
  const [recallRows, setRecallRows] = useState<Record<string, string>[]>([]);
  const [recallTemplate, setRecallTemplate] = useState<ClinicTemplate | null>(null);
  const [recallSentRows, setRecallSentRows] = useState<Record<number, boolean>>({});
  const [recallSendingRows, setRecallSendingRows] = useState<Record<number, boolean>>({});
  const [recallBatchProgress, setRecallBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [expandedRecallRow, setExpandedRecallRow] = useState<number | null>(null);
  const [recallConversationPreviews, setRecallConversationPreviews] = useState<
    Record<string, RecallConversationPreviewState>
  >({});

  /* ------------------------------------------------------------------
     LOAD TEMPLATES
  ------------------------------------------------------------------ */
  useEffect(() => {
    async function loadTemplates() {
      if (!praticienId) return;
      const { data } = await supabase
        .from("clinic_templates")
        .select("id,name,display_name,template_body,placeholders,chakra_template_name,language_code")
        .eq("praticien_id", praticienId)
        .eq("template_kind", "whatsapp");
      if (data) setTemplates(data as ClinicTemplate[]);
    }
    loadTemplates();
  }, [praticienId]);

  /* ------------------------------------------------------------------
     LOAD STATUS MAP
  ------------------------------------------------------------------ */
  useEffect(() => {
    if (!praticienId) return;
    async function loadStatusMap() {
      const { data } = await supabase
        .from("clients")
        .select("plato_appt_status_field, plato_appt_status_map")
        .eq("id", praticienId)
        .single();
      if (!data) return;
      setStatusField(String(data.plato_appt_status_field || ""));
      const rawMap = data.plato_appt_status_map;
      if (rawMap && typeof rawMap === "object") {
        setStatusMap(rawMap as Record<string, string>);
      } else if (typeof rawMap === "string" && rawMap) {
        try { setStatusMap(JSON.parse(rawMap)); } catch { /* ignore */ }
      }
    }
    loadStatusMap();
  }, [praticienId]);

  /* ------------------------------------------------------------------
     LOAD ALREADY SENT TODAY
  ------------------------------------------------------------------ */
  const loadAlreadySentToday = async () => {
    if (!praticienId) return;
    try {
      const res = await fetch(
        `${API_URL}/reminders/logs?praticien_id=${encodeURIComponent(praticienId)}&appointment_date=${encodeURIComponent(appointmentsDate)}`
      );
      if (!res.ok) throw new Error("Failed to load reminder logs");
      const json = await res.json();
      const logs: ReminderLog[] = Array.isArray(json) ? json : json.logs || [];
      setDailyReminderLogs(logs);
      setAlreadySentToday(
        new Set(logs.map((r) => normalizeReminderPhone(r.patient_num)).filter(Boolean))
      );
    } catch {
      const { data } = await supabase
        .from("reminder_logs")
        .select("patient_num")
        .eq("praticien_id", praticienId)
        .eq("appointment_date", appointmentsDate);
      const logs = data || [];
      setDailyReminderLogs([]);
      setAlreadySentToday(
        new Set(logs.map((r: { patient_num: string }) => normalizeReminderPhone(r.patient_num)).filter(Boolean))
      );
    }
  };

  useEffect(() => {
    loadAlreadySentToday();
  }, [praticienId, appointmentsDate]);

  const reminderMessageSids = useMemo(() => {
    const sids = [...dailyReminderLogs, ...reminderLogs]
      .map((log) => (log.message_sid || "").trim())
      .filter(Boolean);
    return Array.from(new Set(sids)).sort().join(",");
  }, [dailyReminderLogs, reminderLogs]);

  useEffect(() => {
    if (!praticienId || !reminderMessageSids) {
      setReminderRealtimeActive(false);
      return;
    }

    const messageSidSet = new Set(reminderMessageSids.split(",").filter(Boolean));

    const channel = supabase
      .channel(`reminders-delivery:${praticienId}:${appointmentsDate}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "conversations",
          filter: `praticien_id=eq.${praticienId}`,
        },
        (payload) => {
          const updated = payload.new as Partial<ReminderLog> | null;
          const messageSid = (updated?.message_sid || "").trim();
          if (!messageSid || !messageSidSet.has(messageSid)) return;

          setDailyReminderLogs((prev) => mergeReminderLogDeliveryUpdate(prev, updated));
          setReminderLogs((prev) => mergeReminderLogDeliveryUpdate(prev, updated));
        }
      )
      .on("system", { event: "*" } as any, (status: string) => {
        if (status === "closed" || status === "errored") {
          setReminderRealtimeActive(false);
        } else if (status === "joined") {
          setReminderRealtimeActive(true);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [praticienId, appointmentsDate, reminderMessageSids]);

  useEffect(() => {
    if (activeTab !== "daily" || !praticienId) return;

    const timer = window.setInterval(() => {
      void loadAlreadySentToday();
    }, reminderRealtimeActive ? 30000 : 10000);

    return () => window.clearInterval(timer);
  }, [activeTab, praticienId, appointmentsDate, reminderRealtimeActive]);

  /* ------------------------------------------------------------------
     LOAD DASHBOARD DATA
  ------------------------------------------------------------------ */
  const loadDashboard = async () => {
    if (!praticienId) return;
    const dashboardScope = activeTab === "recallDashboard" ? "recall" : "standard";
    setDashboardLoading(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      let logsUrl = `${API_URL}/reminders/logs?praticien_id=${praticienId}`;
      let period_days = 30;
      if (periodMode === "date") {
        logsUrl += `&date_from=${dashboardDate}&date_to=${dashboardDate}`;
        period_days = 1;
      } else {
        period_days = periodMode === "7" ? 7 : 30;
        const fromDate = new Date(Date.now() - period_days * 86400000).toISOString().split("T")[0];
        logsUrl += `&date_from=${fromDate}&date_to=${today}`;
      }
      let kpisUrl = `${API_URL}/reminders/kpis?praticien_id=${praticienId}`;
      if (periodMode === "date") {
        kpisUrl += `&date_from=${dashboardDate}&date_to=${dashboardDate}`;
      } else {
        const fromDate = new Date(Date.now() - period_days * 86400000).toISOString().split("T")[0];
        kpisUrl += `&date_from=${fromDate}&date_to=${today}`;
      }
      logsUrl += `&scope=${dashboardScope}`;
      kpisUrl += `&scope=${dashboardScope}`;
      const [kpisRes, logsRes] = await Promise.all([
        fetch(kpisUrl),
        fetch(logsUrl),
      ]);
      const kpisData = await kpisRes.json();
      const logsData = await logsRes.json();
      const logs: ReminderLog[] = Array.isArray(logsData) ? logsData : logsData.logs || [];
      setKpis(kpisData);
      setReminderLogs(logs);
      setLogsDisplayCount(19);
      setDashboardApptStatus({});

      // Charger les statuts Plato uniquement pour les 19 premiers logs affichés
      const firstBatch = logs.slice(0, 19).map((l) => l.appointment_id).filter(Boolean) as string[];
      if (firstBatch.length > 0) {
        try {
          const statusToken = await getAuthToken();
          const statusRes = await fetch(`${API_URL}/plato/appointments/status-batch`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: statusToken ?? "" },
            body: JSON.stringify({ praticien_id: praticienId, appointment_ids: firstBatch }),
          });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            const byApptId: Record<string, string> = statusData.statuses || {};
            const byLogId: Record<string, string> = {};
            logs.forEach((l) => {
              if (l.appointment_id && byApptId[l.appointment_id] !== undefined) {
                byLogId[l.id] = byApptId[l.appointment_id];
              }
            });
            setDashboardApptStatus(byLogId);
            lastPlatoCallRef.current = Date.now();
          }
        } catch {
          // silently ignore status batch errors
        }
      }
    } catch (err) {
      console.error("[Reminders][Dashboard]", err);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "dashboard" || activeTab === "recallDashboard") {
      loadDashboard();
    }
  }, [activeTab, periodMode, dashboardDate, praticienId]);

  /* ------------------------------------------------------------------
     LOAD MORE STATUS (pagination 19 + cooldown 1 min)
  ------------------------------------------------------------------ */
  const loadMoreStatuses = async () => {
    if (!praticienId || statusLoadingMore) return;
    const elapsed = Date.now() - lastPlatoCallRef.current;
    if (elapsed < 60_000) {
      alert(`Please wait ${Math.ceil((60_000 - elapsed) / 1000)}s before loading more statuses.`);
      return;
    }
    const nextCount = logsDisplayCount + 19;
    const nextBatch = filteredLogs
      .slice(logsDisplayCount, nextCount)
      .map((l) => l.appointment_id)
      .filter(Boolean) as string[];
    setLogsDisplayCount(nextCount);
    if (nextBatch.length === 0) return;
    setStatusLoadingMore(true);
    try {
      const statusToken = await getAuthToken();
      const statusRes = await fetch(`${API_URL}/plato/appointments/status-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: statusToken ?? "" },
        body: JSON.stringify({ praticien_id: praticienId, appointment_ids: nextBatch }),
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        const byApptId: Record<string, string> = statusData.statuses || {};
        setDashboardApptStatus((prev) => {
          const updated = { ...prev };
          filteredLogs.forEach((l) => {
            if (l.appointment_id && byApptId[l.appointment_id] !== undefined) {
              updated[l.id] = byApptId[l.appointment_id];
            }
          });
          return updated;
        });
        lastPlatoCallRef.current = Date.now();
      }
    } catch {
      // silently ignore
    } finally {
      setStatusLoadingMore(false);
    }
  };

  /* ------------------------------------------------------------------
     MERGE APPOINTMENTS
  ------------------------------------------------------------------ */
  const mergedRows = useMemo<MergedAppointmentRow[]>(() => {
    const grouped = new Map<string, MergedAppointmentRow>();

    appointmentsItems.forEach((item, idx) => {
      const displayPatientName = item.patient_full_name || item.patient_name || "-";
      const templatePatientName =
        item.patient_template_name || item.patient_preferred_name || displayPatientName;
      const normalizedKey = normalizePatientKey(displayPatientName, item.phone);
      const fallbackKey = item.id || `appointment-${idx}`;
      const key = normalizedKey || fallbackKey;

      const startDate = parseDateSafely(item.start);
      const durationMinutes = getDurationMinutes(item.start, item.end);
      const calendarLabel = item.calendar_name || item.calendar_color || "-";
      const title =
        (item as any).title ||
        extractAppointmentTitle(item.raw) ||
        extractAppointmentTitle((item as Record<string, any>).plato_patient_directory) ||
        extractAppointmentTitle((item as Record<string, any>).patient_directory) ||
        "";

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          patient_name: displayPatientName,
          patient_template_name: templatePatientName,
          patient_full_name: item.patient_full_name,
          patient_preferred_name: item.patient_preferred_name,
          patient_uses_preferred_name: Boolean(item.patient_uses_preferred_name),
          phone: item.phone || "-",
          time: formatTime(item.start),
          sortStart: startDate ? startDate.toISOString() : "",
          durationMinutes,
          calendars: calendarLabel ? [calendarLabel] : ["-"],
          title,
          patient_id: item.patient_id,
          appointment_id: item.id,
          appointment_status: item.appointment_status || undefined,
        });
        return;
      }

      const existing = grouped.get(key);
      if (!existing) return;

      existing.durationMinutes += durationMinutes;

      if (!existing.appointment_status && item.appointment_status) {
        existing.appointment_status = item.appointment_status;
      }

      if (!existing.patient_preferred_name && item.patient_preferred_name) {
        existing.patient_preferred_name = item.patient_preferred_name;
      }

      if (!existing.patient_uses_preferred_name && item.patient_uses_preferred_name) {
        existing.patient_uses_preferred_name = true;
        existing.patient_template_name = templatePatientName;
      }

      if (startDate) {
        const existingDate = parseDateSafely(existing.sortStart);
        if (!existingDate || startDate.getTime() < existingDate.getTime()) {
          existing.sortStart = startDate.toISOString();
          existing.time = formatTime(item.start);
        }
      }

      if (calendarLabel && !existing.calendars.includes(calendarLabel)) {
        existing.calendars.push(calendarLabel);
      }

      if (!existing.title && title) {
        existing.title = title;
      }
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const aDate = parseDateSafely(a.sortStart);
      const bDate = parseDateSafely(b.sortStart);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate.getTime() - bDate.getTime();
    });
  }, [appointmentsItems]);

  /* ------------------------------------------------------------------
     DAILY HANDLERS
  ------------------------------------------------------------------ */
  const initializeReminderDraft = (item: MergedAppointmentRow): ReminderDraft => ({
    title: item.title || "",
    patientName: item.patient_template_name || item.patient_name || "",
    doctorName: item.calendars.length ? item.calendars.join(", ") : "",
    appointmentDate: formatReminderDate(item.sortStart),
    appointmentTime: item.time || "",
    appointmentDuration: formatDurationFromMinutes(item.durationMinutes),
  });

  const handleToggleRow = (item: MergedAppointmentRow) => {
    setReminderDrafts((prev) => {
      if (prev[item.key]) return prev;
      return { ...prev, [item.key]: initializeReminderDraft(item) };
    });
    setExtraDraftVars((prev) => {
      if (prev[item.key]) return prev;
      return { ...prev, [item.key]: {} };
    });
    setPhoneEdits((prev) => {
      if (prev[item.key] !== undefined) return prev;
      const initialPhone = item.phone?.replace("whatsapp:", "") || "";
      return { ...prev, [item.key]: initialPhone === "-" ? "" : initialPhone };
    });
    setExpandedRowKey((current) => (current === item.key ? null : item.key));
  };

  const handleDraftChange = (key: string, field: keyof ReminderDraft, value: string) => {
    setReminderDrafts((prev) => {
      const currentDraft = prev[key];
      if (!currentDraft) return prev;
      return { ...prev, [key]: { ...currentDraft, [field]: value } };
    });
  };

  const handleExtraDraftChange = (rowKey: string, field: string, value: string) => {
    setExtraDraftVars((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [field]: value },
    }));
  };

  const handleValidatePhone = async (rowKey: string, patientId: string | undefined) => {
    const phone = (phoneEdits[rowKey] ?? validatedPhones[rowKey] ?? "").trim();
    if (!phone || !patientId || !praticienId) return;
    setSavingPhones((prev) => ({ ...prev, [rowKey]: true }));
    try {
      await supabase
        .from("plato_patient_directory")
        .update({ telephone: phone })
        .eq("patient_id", patientId)
        .eq("praticien_id", praticienId);
      setValidatedPhones((prev) => ({ ...prev, [rowKey]: phone }));
    } finally {
      setSavingPhones((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const enrichAppointmentsWithTemplatePatientNames = async (
    items: AppointmentItem[],
  ): Promise<AppointmentItem[]> => {
    const patientIds = Array.from(
      new Set(
        items
          .map((item) => String(item.patient_id || "").trim())
          .filter(Boolean),
      ),
    );
    if (!praticienId || patientIds.length === 0) return items;

    try {
      const { data: configRows } = await supabase
        .from("clinic_knowledge")
        .select("value")
        .eq("praticien_id", praticienId)
        .eq("key", "plato_preferred_name_custom_field")
        .limit(1);

      let customFieldCode = String(configRows?.[0]?.value || "").trim();
      const twilioNumero = String(
        user?.twilio_numero || activeDoctor?.twilio_numero || "",
      ).trim();
      if (!customFieldCode && twilioNumero) {
        const { data: twilioConfigRows } = await supabase
          .from("clinic_knowledge")
          .select("value")
          .eq("twilio_numero", twilioNumero)
          .eq("key", "plato_preferred_name_custom_field")
          .limit(1);

        customFieldCode = String(twilioConfigRows?.[0]?.value || "").trim();
      }

      const { data: directoryRows } = await supabase
        .from("plato_patient_directory")
        .select("patient_id,name,raw")
        .eq("praticien_id", praticienId)
        .in("patient_id", patientIds);

      return mapAppointmentsWithPlatoTemplateNames(
        items,
        directoryRows || [],
        customFieldCode,
      ) as AppointmentItem[];
    } catch (error) {
      console.error("[Reminders][PreferredName]", error);
      return items;
    }
  };

  const handleFetchAppointments = async () => {
    if (!praticienId || !appointmentsDate) return;
    setAppointmentsLoading(true);
    setAppointmentsError("");
    try {
      const res = await fetch(`${API_URL}/plato/appointments/by-date`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ praticien_id: praticienId, date: appointmentsDate }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Failed to load appointments");
      const items = Array.isArray(json?.items) ? json.items : [];
      const enrichedItems = await enrichAppointmentsWithTemplatePatientNames(items);
      setAppointmentsItems(enrichedItems);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Unable to fetch appointments";
      setAppointmentsError(msg);
      setAppointmentsItems([]);
    } finally {
      setAppointmentsLoading(false);
    }
  };

  const handleSendReminder = async (rowKey: string) => {
    if (!selectedTemplate || !praticienId) return;
    const draft = reminderDrafts[rowKey];
    const row = mergedRows.find((r) => r.key === rowKey);
    const patientNum = (phoneEdits[rowKey] ?? validatedPhones[rowKey] ?? row?.phone ?? "")
      .replace("whatsapp:", "")
      .trim();
    if (!patientNum || patientNum === "-") {
      alert("Please enter a phone number before sending.");
      return;
    }
    setSendingRows((prev) => ({ ...prev, [rowKey]: true }));
    try {
      const resp = await fetch(`${API_URL}/reminders/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          praticien_id: praticienId,
          patient_num: patientNum,
          patient_name: draft.patientName,
          appointment_id: row?.appointment_id,
          appointment_date: appointmentsDate,
          appointment_time: draft.appointmentTime,
          doctor_name: draft.doctorName,
          reminder_type: "daily",
          template_name: selectedTemplate.name,
          sender_name: templateSenderName,
          variables: {
            salutation: draft.title,
            patient_name: draft.patientName,
            doctor_name: draft.doctorName,
            appointment_date: draft.appointmentDate,
            appointment_time: draft.appointmentTime,
            appointment_duration: draft.appointmentDuration,
            ...(extraDraftVars[rowKey] || {}),
          },
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setSentRows((prev) => ({ ...prev, [rowKey]: true }));
        setDailyReminderLogs((prev) => [
          {
            id: data.reminder_id || `local-${rowKey}-${Date.now()}`,
            patient_num: patientNum,
            patient_name: draft.patientName,
            appointment_id: row?.appointment_id,
            appointment_date: appointmentsDate,
            appointment_time: draft.appointmentTime,
            doctor_name: draft.doctorName,
            reminder_type: "daily",
            template_name: selectedTemplate.name,
            message_sid: data.message_sid,
            sent_at: new Date().toISOString(),
            patient_responded: false,
            follow_up_count: 0,
            delivery_status: "sent",
          },
          ...prev,
        ]);
        setExpandedRowKey(null);
        await loadAlreadySentToday();
        if (Object.keys(statusMap).length > 0 && row?.appointment_id) {
          setStatusUpdatePending({
            appointmentId: row.appointment_id,
            patientName: row.patient_name,
          });
          setPendingStatusCode("");
        }
      }
    } finally {
      setSendingRows((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const handleStatusUpdateConfirm = async () => {
    if (!statusUpdatePending || !pendingStatusCode || !praticienId) return;
    setStatusUpdating(true);
    try {
      const token = await getAuthToken();
      await fetch(`${API_URL}/plato/appointment/update-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({
          praticien_id: praticienId,
          appointment_id: statusUpdatePending.appointmentId,
          status_code: pendingStatusCode,
        }),
      });
    } finally {
      setStatusUpdating(false);
      setStatusUpdatePending(null);
    }
  };

  const handleDashboardStatusUpdate = async (logId: string, appointmentId: string, statusCode: string) => {
    if (!appointmentId || !statusCode || !praticienId) return;
    setDashboardStatusUpdating((prev) => ({ ...prev, [logId]: true }));
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/plato/appointment/update-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({
          praticien_id: praticienId,
          appointment_id: appointmentId,
          status_code: statusCode,
        }),
      });
      if (res.ok) {
        const label = statusMap[statusCode] || statusCode;
        setDashboardApptStatus((prev) => ({ ...prev, [logId]: label }));
      }
    } finally {
      setDashboardStatusUpdating((prev) => ({ ...prev, [logId]: false }));
    }
  };

  /* ------------------------------------------------------------------
     FOLLOW-UP HANDLER
  ------------------------------------------------------------------ */
  const handleFollowUp = async () => {
    if (!followUpModalLog || !followUpTemplate || !praticienId) return;
    setFollowUpSending(true);
    try {
      const resp = await fetch(`${API_URL}/reminders/follow-up`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          praticien_id: praticienId,
          reminder_id: followUpModalLog.id,
          template_name: followUpTemplate.name,
          sender_name: templateSenderName,
          variables: {
            salutation: followUpSalutation,
            patient_name: followUpPatientName,
            doctor_name: followUpDoctorName,
            appointment_date: followUpAppointmentDate,
            appointment_time: followUpAppointmentTime,
            appointment_duration: followUpDuration,
            ...followUpExtraVars,
          },
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setFollowUpModalLog(null);
        await loadDashboard();
      }
    } finally {
      setFollowUpSending(false);
    }
  };

  /* ------------------------------------------------------------------
     MONTHLY HANDLERS
  ------------------------------------------------------------------ */
  const handleScanMonth = async () => {
    if (!praticienId) return;
    const targetMonth = subMonths(scanMonth, 6);
    setMonthlyLoading(true);
    setMonthlyError("");
    setMonthlyPatients([]);
    setScanStats(null);
    try {
      const res = await fetch(`${API_URL}/plato/appointments/monthly`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ praticien_id: praticienId, year_month: targetMonth }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Failed to scan appointments");
      const patients: MonthlyPatient[] = Array.isArray(json.appointments) ? json.appointments : [];
      setMonthlyPatients(patients);
      setScanStats({
        patient_count: json.patient_count ?? patients.length,
        total_pages_fetched: json.total_pages_fetched ?? 0,
        cached: Boolean(json.cached),
      });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Unable to scan monthly appointments";
      setMonthlyError(msg);
    } finally {
      setMonthlyLoading(false);
    }
  };

  const handleSendMonthlyReminder = async (patient: MonthlyPatient) => {
    if (!monthlyTemplate || !praticienId) return;
    const key = patient.patient_id;
    setMonthlySendingRows((prev) => ({ ...prev, [key]: true }));
    try {
      const variables: Record<string, string> = {
        patient_name: patient.patient_name || "",
        doctor_name: patient.doctor_name || "",
        appointment_date: patient.last_visit_date || "",
        appointment_time: "",
        appointment_duration: "",
      };
      const resp = await fetch(`${API_URL}/reminders/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          praticien_id: praticienId,
          patient_num: patient.phone?.replace("whatsapp:", ""),
          patient_name: patient.patient_name,
          appointment_date: patient.last_visit_date,
          doctor_name: patient.doctor_name,
          reminder_type: "monthly_followup",
          template_name: monthlyTemplate.name,
          sender_name: templateSenderName,
          variables,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setMonthlySentRows((prev) => ({ ...prev, [key]: true }));
      }
    } finally {
      setMonthlySendingRows((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleBatchSend = async () => {
    if (!monthlyTemplate || selectedMonthlyPatients.size === 0) return;
    const toSend = monthlyPatients.filter(
      (p) => selectedMonthlyPatients.has(p.patient_id) && !monthlySentRows[p.patient_id]
    );
    setBatchProgress({ current: 0, total: toSend.length });
    for (let i = 0; i < toSend.length; i++) {
      await handleSendMonthlyReminder(toSend[i]);
      setBatchProgress({ current: i + 1, total: toSend.length });
      if (i < toSend.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
    setBatchProgress(null);
  };

  const handleRecallFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRecallFile(file);
    setRecallSentRows({});
    setExpandedRecallRow(null);
    setRecallConversationPreviews({});
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target?.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws);
      const normalized = rows.map((row) => {
        const num = String(row.whatsapp_number ?? "").replace("whatsapp:", "").trim();
        return {
          ...row,
          whatsapp_number: num && !num.startsWith("+") ? `+${num}` : num,
        };
      });
      setRecallRows(normalized);
    };
    reader.readAsBinaryString(file);
  };

  const handleToggleRecallConversation = async (
    row: Record<string, string>,
    index: number,
  ) => {
    if (expandedRecallRow === index) {
      setExpandedRecallRow(null);
      return;
    }

    setExpandedRecallRow(index);
    const identifier = normalizeRecallConversationIdentifier(row.whatsapp_number);
    if (!identifier) return;

    const cached = recallConversationPreviews[identifier];
    if (cached && !cached.error) return;

    setRecallConversationPreviews((previous) => ({
      ...previous,
      [identifier]: { loading: true, messages: [], error: "" },
    }));

    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error("Authentication required.");
      if (!practKey) throw new Error("Conversation key is not available yet.");

      const response = await fetch(
        `${API_URL}/internal/conversations/${encodeURIComponent(identifier)}` +
          `?praticien_id=${encodeURIComponent(praticienId || "")}&limit=5`,
        { headers: { Authorization: authToken } },
      );
      if (!response.ok) throw new Error("Unable to load recent exchanges.");

      const payload = await response.json();
      const messages = prepareRecallConversationMessages(
        (Array.isArray(payload?.messages)
          ? payload.messages
          : []) as RecallConversationMessage[],
      );
      const decryptedMessages = await Promise.all(
        messages.map(async (message) => {
          const encryptedContent = String(message.contenu_chiffre || "");
          if (!encryptedContent.startsWith("gAAAA")) {
            return { ...message, content: encryptedContent };
          }
          try {
            return {
              ...message,
              content: await decryptFernet(practKey, encryptedContent),
            };
          } catch {
            return { ...message, content: "Message unavailable." };
          }
        }),
      );

      setRecallConversationPreviews((previous) => ({
        ...previous,
        [identifier]: {
          loading: false,
          messages: decryptedMessages,
          error: "",
        },
      }));
    } catch (error) {
      setRecallConversationPreviews((previous) => ({
        ...previous,
        [identifier]: {
          loading: false,
          messages: [],
          error:
            error instanceof Error
              ? error.message
              : "Unable to load recent exchanges.",
        },
      }));
    }
  };

  const handleSendRecallReminder = async (row: Record<string, string>, index: number) => {
    if (!recallTemplate || !praticienId) return;
    setRecallSendingRows((prev) => ({ ...prev, [index]: true }));
    try {
      const { whatsapp_number, ...variables } = row;
      const resp = await fetch(`${API_URL}/reminders/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          praticien_id: praticienId,
          patient_num: String(whatsapp_number).replace("whatsapp:", ""),
          patient_name: variables.patient_name || "",
          appointment_date: variables.last_visit_date || "",
          doctor_name: variables.doctor_name || "",
          reminder_type: "recall",
          template_name: recallTemplate.name,
          sender_name: templateSenderName,
          variables,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setRecallSentRows((prev) => ({ ...prev, [index]: true }));
      }
    } finally {
      setRecallSendingRows((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleRecallBatchSend = async () => {
    if (!recallTemplate || recallRows.length === 0) return;
    const toSend = recallRows
      .map((row, i) => ({ row, i }))
      .filter(({ i }) => !recallSentRows[i]);
    setRecallBatchProgress({ current: 0, total: toSend.length });
    for (let idx = 0; idx < toSend.length; idx++) {
      const { row, i } = toSend[idx];
      await handleSendRecallReminder(row, i);
      setRecallBatchProgress({ current: idx + 1, total: toSend.length });
      if (idx < toSend.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
    setRecallBatchProgress(null);
  };

  /* ------------------------------------------------------------------
     DERIVED
  ------------------------------------------------------------------ */
  const filteredLogs = useMemo(() => {
    if (logsFilter === "noreply") return reminderLogs.filter((r) => !r.patient_responded);
    if (logsFilter === "replied") return reminderLogs.filter((r) => r.patient_responded);
    return reminderLogs;
  }, [reminderLogs, logsFilter]);

  const deliveryStatusBadge = (status?: string) => {
    const normalized = normalizeReminderDeliveryStatus(status);
    const label = getReminderDeliveryLabel(status);
    switch (normalized) {
      case "received":
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">{label}</span>;
      case "read":
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">{label}</span>;
      case "sent":
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{label}</span>;
      case "failed":
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">{label}</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{label}</span>;
    }
  };

  const appointmentStatusLabel = (status?: string) => {
    const label = status?.trim() || "";
    return <span className="text-xs text-slate-700">{label || "Unknown"}</span>;
  };

  /* ------------------------------------------------------------------
     RENDER
  ------------------------------------------------------------------ */
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-xl font-semibold text-slate-900 mb-6">Reminders</h1>

        {/* Tabs */}
        <div className="flex border-b mb-6">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "daily"
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("daily")}
          >
            Daily Reminders
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "dashboard"
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("dashboard")}
          >
            Dashboard
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "recallDashboard"
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("recallDashboard")}
          >
            Recall Dashboard
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "monthly"
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("monthly")}
          >
            Monthly Follow-ups
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "recall"
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("recall")}
          >
            Recall Import
          </button>
        </div>

        {/* ===== TAB 1: RAPPELS DU JOUR ===== */}
        {activeTab === "daily" && (
          <div className="space-y-5">
            {/* Template selector */}
            <div>
              <label className="block text-sm font-medium mb-1">Message template</label>
              <select
                value={selectedTemplate?.id || ""}
                onChange={(e) => {
                  const t = templates.find((t) => t.id === e.target.value) || null;
                  setSelectedTemplate(t);
                }}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
              >
                <option value="">Select a message template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.display_name ?? t.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date picker */}
            <div>
              <label className="block text-sm font-medium mb-1">Appointment date</label>
              <input
                type="date"
                value={appointmentsDate}
                onChange={(e) => setAppointmentsDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
              />
            </div>

            <button
              onClick={handleFetchAppointments}
              disabled={appointmentsLoading || !appointmentsDate}
              className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed w-full text-sm"
            >
              {appointmentsLoading ? "Loading..." : "Load appointments"}
            </button>

            {appointmentsError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
                {appointmentsError}
              </div>
            )}

            {!appointmentsLoading && !appointmentsError && (
              <p className="text-sm text-gray-600">{mergedRows.length} appointment{mergedRows.length !== 1 ? "s" : ""}</p>
            )}

            {appointmentsLoading && (
              <p className="text-sm text-gray-500">Loading appointments...</p>
            )}

            {!appointmentsLoading && !appointmentsError && mergedRows.length === 0 && appointmentsItems.length === 0 && (
              <p className="text-sm text-gray-500">No appointments found for this date.</p>
            )}

            {!appointmentsLoading && mergedRows.length > 0 && (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="border-b font-medium bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left w-10"></th>
                      <th className="px-3 py-2 text-left">Time</th>
                      <th className="px-3 py-2 text-left">Patient</th>
                      <th className="px-3 py-2 text-left">Doctor / Calendar</th>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Duration</th>
                      <th className="px-3 py-2 text-left">Appt. Status</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedRows.map((item) => {
                      const reminderPhone = phoneEdits[item.key] ?? item.phone;
                      const latestReminderLog = findLatestReminderLogForRow(
                        { appointment_id: item.appointment_id, phone: reminderPhone },
                        dailyReminderLogs
                      );
                      const phoneClean = normalizeReminderPhone(reminderPhone);
                      const hasReminder =
                        Boolean(latestReminderLog) ||
                        alreadySentToday.has(phoneClean) ||
                        Boolean(sentRows[item.key]);
                      const deliveryStatus =
                        latestReminderLog?.delivery_status || (hasReminder ? "sent" : undefined);
                      const rowClass = hasReminder
                        ? isReminderDeliveryFailure(deliveryStatus)
                          ? "bg-red-50 text-red-900"
                          : "bg-green-50 text-green-900"
                        : "last:border-0";
                      const patientNameLabel = item.patient_uses_preferred_name
                        ? "Preferred name"
                        : "Patient name";
                      return (
                        <Fragment key={item.key}>
                          <tr
                            className={`border-b ${rowClass}`}
                          >
                            <td className="px-3 py-2 align-top">
                              <button
                                type="button"
                                onClick={() => handleToggleRow(item)}
                                className="h-6 w-6 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                                aria-label={`Toggle reminder details for ${item.patient_name}`}
                              >
                                {expandedRowKey === item.key ? "−" : "+"}
                              </button>
                            </td>
                            <td className="px-3 py-2">{item.time || "-"}</td>
                            <td className="px-3 py-2">{item.patient_name || "-"}</td>
                            <td className="px-3 py-2">
                              {item.calendars.length ? item.calendars.join(", ") : "-"}
                            </td>
                            <td className="px-3 py-2">{item.phone || "-"}</td>
                            <td className="px-3 py-2">
                              {formatDurationFromMinutes(item.durationMinutes)}
                            </td>
                            <td className="px-3 py-2">
                              {appointmentStatusLabel(item.appointment_status)}
                            </td>
                            <td className="px-3 py-2">
                              {deliveryStatusBadge(deliveryStatus)}
                            </td>
                          </tr>

                          {expandedRowKey === item.key && reminderDrafts[item.key] && (
                            <tr className="border-b bg-slate-50/70">
                              <td colSpan={8} className="p-3">
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-4">
                                  {(() => {
                                    const rawPhone = item.phone?.replace("whatsapp:", "") || "";
                                    const currentEdit = phoneEdits[item.key] ?? validatedPhones[item.key] ?? (rawPhone === "-" ? "" : rawPhone);
                                    const phoneNeedsPlus =
                                      currentEdit && !currentEdit.startsWith("+") && !currentEdit.startsWith("whatsapp:+");
                                    const savedPhone = validatedPhones[item.key];
                                    const isSaved = Boolean(savedPhone && savedPhone === currentEdit);
                                    return (
                                      <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                                        <div className="flex items-center justify-between gap-3">
                                          <label className="text-xs font-medium text-slate-700">
                                            Phone used for this reminder
                                          </label>
                                          {phoneNeedsPlus && (
                                            <span className="text-xs text-amber-700">
                                              Add + and country code if needed
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={currentEdit}
                                            onChange={(e) =>
                                              setPhoneEdits((prev) => ({ ...prev, [item.key]: e.target.value }))
                                            }
                                            className={`flex-1 border rounded-lg p-2 text-sm font-mono ${phoneNeedsPlus ? "border-amber-300 bg-amber-50" : "border-slate-300 bg-white"}`}
                                          />
                                          {isSaved ? (
                                            <span className="inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                                              Saved
                                            </span>
                                          ) : item.patient_id ? (
                                            <button
                                              type="button"
                                              onClick={() => handleValidatePhone(item.key, item.patient_id)}
                                              disabled={savingPhones[item.key] || !currentEdit}
                                              className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                                            >
                                              {savingPhones[item.key] ? "Saving..." : "Save"}
                                            </button>
                                          ) : (
                                            <span className="text-xs text-slate-500 whitespace-nowrap">
                                              Send only
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {!selectedTemplate && (
                                    <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                                      Please select a message template before sending.
                                    </p>
                                  )}

                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    <div>
                                      <label className="block text-xs font-medium text-slate-700 mb-1">
                                        Salutation
                                      </label>
                                      <select
                                        value={reminderDrafts[item.key].title}
                                        onChange={(e) =>
                                          handleDraftChange(item.key, "title", e.target.value)
                                        }
                                        className="w-full border border-slate-300 rounded-lg p-2 text-sm bg-white"
                                      >
                                        <option value="">No title</option>
                                        {REMINDER_TITLE_OPTIONS.filter((o) => o !== "").map((o) => (
                                          <option key={o} value={o}>
                                            {o}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {(
                                      [
                                        { key: "patientName", label: patientNameLabel },
                                        { key: "doctorName", label: "Doctor" },
                                        { key: "appointmentDate", label: "Appointment date" },
                                        { key: "appointmentTime", label: "Appointment time" },
                                        { key: "appointmentDuration", label: "Duration" },
                                      ] as { key: keyof ReminderDraft; label: string }[]
                                    ).map(({ key, label }) => (
                                      <div key={key}>
                                        <label className="block text-xs font-medium text-slate-700 mb-1">
                                          {label}
                                        </label>
                                        <input
                                          type="text"
                                          value={reminderDrafts[item.key][key]}
                                          onChange={(e) =>
                                            handleDraftChange(item.key, key, e.target.value)
                                          }
                                          className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                                        />
                                      </div>
                                    ))}

                                    {selectedTemplate &&
                                      getExtraPlaceholders(selectedTemplate.placeholders).map((placeholder) => (
                                        <div key={placeholder}>
                                          <label className="block text-xs font-medium text-slate-700 mb-1">
                                            {placeholder.replace(/_/g, " ")}
                                          </label>
                                          <input
                                            type="text"
                                            value={extraDraftVars[item.key]?.[placeholder] || ""}
                                            onChange={(e) => handleExtraDraftChange(item.key, placeholder, e.target.value)}
                                            className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                                            placeholder={`Enter ${placeholder.replace(/_/g, " ")}`}
                                          />
                                        </div>
                                      ))}
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => handleSendReminder(item.key)}
                                    disabled={
                                      sendingRows[item.key] ||
                                      !selectedTemplate
                                    }
                                    className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                                  >
                                    {sendingRows[item.key]
                                      ? "Sending..."
                                      : hasReminder
                                      ? "Resend reminder"
                                      : "Send reminder"}
                                  </button>

                                  {selectedTemplate && (
                                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                        Message preview
                                      </p>
                                      <div className="text-sm text-slate-800 whitespace-pre-line">
                                        {renderPreview(
                                          selectedTemplate.template_body,
                                          selectedTemplate.placeholders,
                                          {
                                            salutation: reminderDrafts[item.key].title,
                                            patient_name: reminderDrafts[item.key].patientName,
                                            doctor_name: reminderDrafts[item.key].doctorName,
                                            appointment_date: reminderDrafts[item.key].appointmentDate,
                                            appointment_time: reminderDrafts[item.key].appointmentTime,
                                            appointment_duration: reminderDrafts[item.key].appointmentDuration,
                                            ...(extraDraftVars[item.key] || {}),
                                          }
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== TAB 2: TABLEAU DE BORD ===== */}
        {(activeTab === "dashboard" || activeTab === "recallDashboard") && (
          <div className="space-y-6">
            {/* Period toggle */}
            <div className="flex items-center gap-2 flex-wrap">
              {(["7", "30", "date"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setPeriodMode(mode)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition ${
                    periodMode === mode
                      ? "bg-slate-900 text-white border-slate-900"
                      : "border-gray-200 text-slate-600 hover:border-gray-300"
                  }`}
                >
                  {mode === "date" ? "Date" : `${mode}d`}
                </button>
              ))}
              {periodMode === "date" && (
                <input
                  type="date"
                  value={dashboardDate}
                  onChange={(e) => setDashboardDate(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                />
              )}
            </div>

            {/* KPI cards */}
            {dashboardLoading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : kpis ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Sent</p>
                  <p className="text-2xl font-bold text-slate-900">{kpis.total_sent}</p>
                  <p className="text-xs text-slate-500 mt-1">reminders sent</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Delivery rate</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {Math.round(kpis.delivery_rate * 100)}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{kpis.delivered_count} delivered</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Response rate</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {Math.round(kpis.response_rate * 100)}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{kpis.responded_count} replied</p>
                </div>
                <div
                  className={`border rounded-xl p-4 ${
                    kpis.pending_follow_up_count > 0
                      ? "bg-red-50 border-red-200"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Needs follow-up</p>
                  <p
                    className={`text-2xl font-bold ${
                      kpis.pending_follow_up_count > 0 ? "text-red-700" : "text-slate-900"
                    }`}
                  >
                    {kpis.pending_follow_up_count}
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      kpis.pending_follow_up_count > 0 ? "text-red-500" : "text-slate-500"
                    }`}
                  >
                    no reply after 24h
                  </p>
                </div>
              </div>
            ) : null}

            {/* Filter pills + refresh */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex gap-2">
                {(
                  [
                    { value: "all", label: "All" },
                    { value: "noreply", label: "No reply" },
                    { value: "replied", label: "Replied" },
                  ] as { value: typeof logsFilter; label: string }[]
                ).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setLogsFilter(value)}
                    className={`px-3 py-1 rounded-full text-sm font-medium border transition ${
                      logsFilter === value
                        ? "bg-slate-900 text-white border-slate-900"
                        : "border-gray-200 text-slate-600 hover:border-gray-300"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={loadDashboard}
                disabled={dashboardLoading}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-gray-200 text-sm text-slate-600 hover:border-gray-300 hover:text-slate-900 disabled:opacity-50 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`w-3.5 h-3.5 ${dashboardLoading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>

            {/* Logs table */}
            {filteredLogs.length === 0 ? (
              <p className="text-sm text-gray-500">No reminders found.</p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="border-b font-medium bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Patient</th>
                      <th className="px-3 py-2 text-left">Appointment</th>
                      <th className="px-3 py-2 text-left">Sent on</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      {Object.keys(statusMap).length > 0 && (
                        <>
                          <th className="px-3 py-2 text-left">Appt. status</th>
                          <th className="px-3 py-2 text-left">Appt. status selection</th>
                        </>
                      )}
                      <th className="px-3 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.slice(0, logsDisplayCount).map((log) => (
                      <tr key={log.id} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <button
                            onClick={() =>
                              (window.location.href =
                                "/conversations?phone=" + encodeURIComponent(log.patient_num))
                            }
                            className="text-blue-600 hover:underline text-left"
                          >
                            {log.patient_name || log.patient_num}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          {log.appointment_date}
                          {log.appointment_time ? ` ${log.appointment_time}` : ""}
                        </td>
                        <td className="px-3 py-2">
                          {new Date(log.sent_at).toLocaleDateString("en-SG")}
                        </td>
                        <td className="px-3 py-2">
                          {log.patient_responded ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Replied
                            </span>
                          ) : (
                            deliveryStatusBadge(log.delivery_status)
                          )}
                        </td>
                        {Object.keys(statusMap).length > 0 && (
                          <>
                          <td className="px-3 py-2">
                            <span className="text-xs text-slate-700">
                              {dashboardApptStatus[log.id] || "—"}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {log.appointment_id ? (
                              <div className="flex items-center gap-1">
                                <select
                                  value={dashboardStatusSelected[log.id] ?? ""}
                                  disabled={dashboardStatusUpdating[log.id]}
                                  onChange={(e) =>
                                    setDashboardStatusSelected((prev) => ({ ...prev, [log.id]: e.target.value }))
                                  }
                                  className="border border-gray-200 rounded px-1 py-0.5 text-xs max-w-[130px]"
                                >
                                  <option value="">— Select —</option>
                                  {Object.entries(statusMap).map(([code, label]) => (
                                    <option key={code} value={code}>{label}</option>
                                  ))}
                                </select>
                                {dashboardStatusSelected[log.id] && (
                                  dashboardStatusUpdating[log.id] ? (
                                    <span className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin inline-block" />
                                  ) : (
                                    <button
                                      onClick={() => {
                                        handleDashboardStatusUpdate(log.id, log.appointment_id!, dashboardStatusSelected[log.id]);
                                        setDashboardStatusSelected((prev) => ({ ...prev, [log.id]: "" }));
                                      }}
                                      className="text-xs px-1.5 py-0.5 rounded bg-slate-900 text-white hover:bg-slate-700"
                                    >
                                      OK
                                    </button>
                                  )
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          </>
                        )}
                        <td className="px-3 py-2">
                          <button
                            onClick={() => {
                              const matchedRow = log.appointment_id
                                ? mergedRows.find((r) => r.appointment_id === log.appointment_id)
                                : undefined;
                              setFollowUpModalLog(log);
                              setFollowUpTemplate(null);
                              setFollowUpSalutation("");
                              setFollowUpPatientName(log.patient_name || "");
                              setFollowUpDoctorName(log.doctor_name || "");
                              setFollowUpAppointmentDate(formatReminderDate(log.appointment_date));
                              setFollowUpAppointmentTime(log.appointment_time || "");
                              setFollowUpDuration(matchedRow ? formatDurationFromMinutes(matchedRow.durationMinutes) : "");
                              setFollowUpExtraVars({});
                            }}
                            disabled={
                              log.patient_responded || log.delivery_status === "failed"
                            }
                            className="text-xs px-2 py-1 rounded border border-gray-200 text-slate-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Follow up
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {filteredLogs.length > logsDisplayCount && (
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={loadMoreStatuses}
                  disabled={statusLoadingMore}
                  className="px-4 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {statusLoadingMore ? "Loading…" : `Load next 19 (${filteredLogs.length - logsDisplayCount} remaining)`}
                </button>
                {lastPlatoCallRef.current > 0 && Date.now() - lastPlatoCallRef.current < 60_000 && (
                  <span className="text-xs text-gray-400">Wait 1 min between batches (Plato rate limit)</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== TAB 3: RAPPELS MENSUELS ===== */}
        {activeTab === "monthly" && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1">Reference month</label>
              <input
                type="month"
                value={scanMonth}
                onChange={(e) => setScanMonth(e.target.value)}
                className="border border-gray-200 rounded-lg p-2 text-sm"
              />
              <p className="mt-1 text-xs text-slate-500">
                Patients to re-contact: those seen in{" "}
                <span className="font-medium">{formatMonthLabel(subMonths(scanMonth, 6))}</span>
              </p>
            </div>

            <button
              onClick={handleScanMonth}
              disabled={monthlyLoading}
              className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
            >
              {monthlyLoading ? "Fetching data..." : "Scan"}
            </button>

            {monthlyLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></span>
                Fetching Plato data...
              </div>
            )}

            {monthlyError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
                {monthlyError}
              </div>
            )}

            {scanStats && (
              <p className="text-sm text-gray-600">
                {scanStats.patient_count} patient{scanStats.patient_count !== 1 ? "s" : ""} found ({scanStats.total_pages_fetched} page{scanStats.total_pages_fetched !== 1 ? "s" : ""} fetched)
                {scanStats.cached ? " — cached results" : ""}
              </p>
            )}

            {monthlyPatients.length > 0 && (
              <div className="space-y-4">
                {/* Template selector */}
                <div>
                  <label className="block text-sm font-medium mb-1">Message template</label>
                  <select
                    value={monthlyTemplate?.id || ""}
                    onChange={(e) => {
                      const t = templates.find((t) => t.id === e.target.value) || null;
                      setMonthlyTemplate(t);
                    }}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                  >
                    <option value="">Select a message template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Select all + batch send */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        selectedMonthlyPatients.size === monthlyPatients.length &&
                        monthlyPatients.length > 0
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMonthlyPatients(
                            new Set(monthlyPatients.map((p) => p.patient_id))
                          );
                        } else {
                          setSelectedMonthlyPatients(new Set());
                        }
                      }}
                    />
                    Select all
                  </label>

                  <button
                    onClick={handleBatchSend}
                    disabled={
                      !monthlyTemplate ||
                      selectedMonthlyPatients.size === 0 ||
                      batchProgress !== null
                    }
                    className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                  >
                    {batchProgress
                      ? `Sending ${batchProgress.current}/${batchProgress.total}...`
                      : `Send to ${selectedMonthlyPatients.size} selected`}
                  </button>
                </div>

                {/* Monthly patients table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="border-b font-medium bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left w-10"></th>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Phone</th>
                        <th className="px-3 py-2 text-left">Visits</th>
                        <th className="px-3 py-2 text-left">Last visit</th>
                        <th className="px-3 py-2 text-left">Doctor</th>
                        <th className="px-3 py-2 text-left">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyPatients.map((patient) => {
                        const key = patient.patient_id;
                        const isSent = Boolean(monthlySentRows[key]);
                        const isSending = Boolean(monthlySendingRows[key]);
                        return (
                          <tr
                            key={key}
                            className={`border-b last:border-0 ${isSent ? "bg-green-50" : ""}`}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedMonthlyPatients.has(key)}
                                disabled={isSent}
                                onChange={(e) => {
                                  setSelectedMonthlyPatients((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(key);
                                    else next.delete(key);
                                    return next;
                                  });
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">{patient.patient_name || "-"}</td>
                            <td className="px-3 py-2">{patient.phone || "-"}</td>
                            <td className="px-3 py-2">{patient.visit_count ?? "-"}</td>
                            <td className="px-3 py-2">
                              {patient.last_visit_date
                                ? formatReminderDate(patient.last_visit_date)
                                : "-"}
                            </td>
                            <td className="px-3 py-2">{patient.doctor_name || "-"}</td>
                            <td className="px-3 py-2">
                              {isSent ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Sent ✓
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleSendMonthlyReminder(patient)}
                                  disabled={isSending || !monthlyTemplate}
                                  className="text-xs px-2 py-1 rounded border border-gray-200 text-slate-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {isSending ? "Sending..." : "Send"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== TAB 4: RECALL IMPORT ===== */}
        {activeTab === "recall" && (
          <div className="space-y-5">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">Excel file format</p>
              <p>Required column: <code className="bg-blue-100 px-1 rounded">whatsapp_number</code> (E.164 format, e.g. +6591234567)</p>
              <p className="mt-0.5">Other columns become template variables — name them exactly as the template placeholders (e.g. <code className="bg-blue-100 px-1 rounded">patient_name</code>, <code className="bg-blue-100 px-1 rounded">doctor_name</code>, <code className="bg-blue-100 px-1 rounded">last_visit_date</code>).</p>
            </div>

            {/* File upload */}
            <div>
              <label className="block text-sm font-medium mb-1">Import Excel file (.xlsx / .xls)</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleRecallFileUpload}
                className="block text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-gray-200 file:text-sm file:bg-white file:text-gray-700 hover:file:bg-gray-50"
              />
              {recallFile && (
                <p className="mt-1 text-xs text-slate-500">
                  {recallFile.name} — {recallRows.length} patient{recallRows.length !== 1 ? "s" : ""} detected
                </p>
              )}
            </div>

            {recallRows.length > 0 && (
              <div className="space-y-4">
                {/* Template selector */}
                <div>
                  <label className="block text-sm font-medium mb-1">Message template</label>
                  <select
                    value={recallTemplate?.id || ""}
                    onChange={(e) => {
                      const t = templates.find((t) => t.id === e.target.value) || null;
                      setRecallTemplate(t);
                    }}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                  >
                    <option value="">Select a message template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.display_name || t.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Batch send button + progress */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleRecallBatchSend}
                    disabled={!recallTemplate || recallBatchProgress !== null}
                    className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                  >
                    {recallBatchProgress
                      ? `Sending ${recallBatchProgress.current}/${recallBatchProgress.total}...`
                      : `Send to all ${recallRows.filter((_, i) => !recallSentRows[i]).length} patients`}
                  </button>
                  {recallBatchProgress && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></span>
                      Sending with 2s delay between messages...
                    </div>
                  )}
                </div>

                {/* Preview table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b font-medium bg-gray-50 sticky top-0">
                      <tr>
                        {Object.keys(recallRows[0]).map((col) => (
                          <th key={col} className="px-3 py-2 text-left whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-left">Recent exchanges</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recallRows.map((row, i) => {
                        const isSent = Boolean(recallSentRows[i]);
                        const isSending = Boolean(recallSendingRows[i]);
                        const isExpanded = expandedRecallRow === i;
                        const identifier = normalizeRecallConversationIdentifier(
                          row.whatsapp_number,
                        );
                        const preview = identifier
                          ? recallConversationPreviews[identifier]
                          : undefined;
                        return (
                          <Fragment key={i}>
                            <tr className={`border-b ${isSent ? "bg-green-50" : ""}`}>
                              {Object.values(row).map((val, j) => (
                                <td key={j} className="px-3 py-2 whitespace-nowrap">
                                  {String(val)}
                                </td>
                              ))}
                              <td className="px-3 py-2 whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => handleToggleRecallConversation(row, i)}
                                  aria-expanded={isExpanded}
                                  aria-label={`${isExpanded ? "Hide" : "Show"} recent exchanges for ${row.patient_name || row.whatsapp_number}`}
                                  className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-950"
                                >
                                  <span aria-hidden="true">{isExpanded ? "▼" : "▶"}</span>
                                  {isExpanded ? "Hide" : "View"}
                                </button>
                              </td>
                              <td className="px-3 py-2">
                                {isSent ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                    Sent ✓
                                  </span>
                                ) : isSending ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                    <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></span>
                                    Sending...
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleSendRecallReminder(row, i)}
                                    disabled={!recallTemplate}
                                    className="text-xs px-2 py-1 rounded border border-gray-200 text-slate-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Send
                                  </button>
                                )}
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr className="border-b bg-slate-50/70">
                                <td
                                  colSpan={Object.keys(recallRows[0]).length + 2}
                                  className="px-4 py-3"
                                >
                                  {!identifier ? (
                                    <p className="text-xs text-amber-700">
                                      A valid WhatsApp number is required.
                                    </p>
                                  ) : preview?.loading ? (
                                    <p className="text-xs text-slate-500">
                                      Loading recent exchanges...
                                    </p>
                                  ) : preview?.error ? (
                                    <p className="text-xs text-red-700">{preview.error}</p>
                                  ) : preview && preview.messages.length === 0 ? (
                                    <p className="text-xs text-slate-500">
                                      No previous exchanges found.
                                    </p>
                                  ) : preview ? (
                                    <div className="space-y-2">
                                      {preview.messages.map((message) => {
                                        const fromPatient = message.role === "user";
                                        return (
                                          <div
                                            key={message.id}
                                            className={`max-w-2xl rounded-lg px-3 py-2 ${
                                              fromPatient
                                                ? "bg-white border border-slate-200"
                                                : "ml-auto bg-blue-50 border border-blue-100"
                                            }`}
                                          >
                                            <div className="flex items-center justify-between gap-3 text-[11px] text-slate-500">
                                              <span className="font-medium text-slate-700">
                                                {getRecallConversationRoleLabel(message.role)}
                                              </span>
                                              <time dateTime={message.created_at}>
                                                {new Date(message.created_at).toLocaleString()}
                                              </time>
                                            </div>
                                            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">
                                              {message.content || "Message unavailable."}
                                            </p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : null}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== STATUS UPDATE MODAL (après envoi reminder) ===== */}
      {statusUpdatePending && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4"
          onClick={() => setStatusUpdatePending(null)}
        >
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p className="text-base font-semibold text-slate-900">Reminder sent!</p>
              <p className="text-sm text-slate-500 mt-1">
                Update appointment status for{" "}
                <span className="font-medium">{statusUpdatePending.patientName}</span>?
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Appointment status</label>
              <select
                value={pendingStatusCode}
                onChange={(e) => setPendingStatusCode(e.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
              >
                <option value="">— Select a status —</option>
                {Object.entries(statusMap).map(([code, label]) => (
                  <option key={code} value={code}>{label} ({code})</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setStatusUpdatePending(null)}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-slate-700 hover:bg-gray-50"
              >
                Skip
              </button>
              <button
                onClick={handleStatusUpdateConfirm}
                disabled={!pendingStatusCode || statusUpdating}
                className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {statusUpdating ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== FOLLOW-UP MODAL ===== */}
      {followUpModalLog && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4"
          onClick={() => setFollowUpModalLog(null)}
        >
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500">Send follow-up to</p>
                <p className="text-base font-semibold text-slate-800">
                  {followUpModalLog.patient_name || followUpModalLog.patient_num}
                </p>
              </div>
              <button
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => setFollowUpModalLog(null)}
              >
                Close
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Message template</label>
              <select
                value={followUpTemplate?.id || ""}
                onChange={(e) => {
                  const t = templates.find((t) => t.id === e.target.value) || null;
                  setFollowUpTemplate(t);
                }}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
              >
                <option value="">Select a template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.display_name ?? t.name}
                  </option>
                ))}
              </select>
            </div>

            {followUpTemplate && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Salutation</label>
                  <select
                    value={followUpSalutation}
                    onChange={(e) => setFollowUpSalutation(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm bg-white"
                  >
                    <option value="">No title</option>
                    {REMINDER_TITLE_OPTIONS.filter((o) => o !== "").map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>

                {(
                  [
                    { state: followUpPatientName, setter: setFollowUpPatientName, label: "Patient name" },
                    { state: followUpDoctorName, setter: setFollowUpDoctorName, label: "Doctor" },
                    { state: followUpAppointmentDate, setter: setFollowUpAppointmentDate, label: "Appointment date" },
                    { state: followUpAppointmentTime, setter: setFollowUpAppointmentTime, label: "Appointment time" },
                    { state: followUpDuration, setter: setFollowUpDuration, label: "Duration" },
                  ] as { state: string; setter: (v: string) => void; label: string }[]
                ).map(({ state, setter, label }) => (
                  <div key={label}>
                    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setter(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                    />
                  </div>
                ))}

                {getExtraPlaceholders(followUpTemplate.placeholders).map((placeholder) => (
                  <div key={placeholder}>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      {placeholder.replace(/_/g, " ")}
                    </label>
                    <input
                      type="text"
                      value={followUpExtraVars[placeholder] || ""}
                      onChange={(e) =>
                        setFollowUpExtraVars((prev) => ({ ...prev, [placeholder]: e.target.value }))
                      }
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                      placeholder={`Enter ${placeholder.replace(/_/g, " ")}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {followUpTemplate && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Preview
                </p>
                <p className="text-sm text-slate-700 whitespace-pre-line">
                  {renderPreview(
                    followUpTemplate.template_body,
                    followUpTemplate.placeholders,
                    {
                      salutation: followUpSalutation,
                      patient_name: followUpPatientName,
                      doctor_name: followUpDoctorName,
                      appointment_date: followUpAppointmentDate,
                      appointment_time: followUpAppointmentTime,
                      appointment_duration: followUpDuration,
                      ...followUpExtraVars,
                    }
                  )}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setFollowUpModalLog(null)}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-slate-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFollowUp}
                disabled={!followUpTemplate || followUpSending}
                className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {followUpSending ? "Sending..." : "Confirm follow-up"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
